import { z } from 'zod';
import type {
  UnifiedRuleDefinition,
  UnifiedRule,
  UnifiedIncident,
  UnifiedCapability,
  UnifiedTimelineEvent,
  UnifiedHeartbeat,
  ConditionAssessment,
} from '../../../../functions/src/monitoring/unified/model';

// Validate the callable boundary in the browser; server modules stay outside the browser bundle.
export const alertModeSchema = z.enum(['live', 'test', 'not_in_use']);
export const workStatusSchema = z.enum(['open', 'acknowledged', 'in_progress', 'resolved']);
const finite = z.number().finite();
const timestamp = finite.nullable();
const cursor = z.string().min(1).nullable();

export const conditionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('no_data'), maxAgeMinutes: finite.min(1).max(525600) }),
  z.object({
    kind: z.literal('numeric'),
    metric: z.string().min(1),
    operator: z.enum(['below', 'above']),
    threshold: finite,
    recoveryThreshold: finite.nullable(),
    thresholdSource: z.enum(['rule', 'device']),
    comparison: z.enum(['inclusive', 'strict']).optional(),
    recoveryComparison: z.enum(['inclusive', 'strict']).optional(),
    roundingDecimals: finite.int().min(0).max(6).optional(),
    recoveryDeltaFromOpening: finite.positive().optional(),
  }),
  z.object({ kind: z.literal('fault'), key: z.string().min(1) }),
  z.object({ kind: z.literal('message'), contains: z.string().trim().min(1).max(200) }),
  z.object({
    kind: z.literal('delta'),
    metric: z.string().min(1),
    threshold: finite.positive(),
    windowMinutes: finite.min(1).max(10080),
  }),
]);
export const ruleDefinitionSchema = z.object({
  title: z.string().trim().min(1, 'Skriv et navn til reglen.').max(120),
  description: z.string().max(1000),
  enabled: z.boolean(),
  mode: alertModeSchema,
  scope: z.object({
    sensorTypes: z.array(z.string().min(1)).min(1, 'Vælg mindst én enhedstype.'),
    orgId: z.string().nullable(),
    sensorId: z.string().nullable(),
  }),
  condition: conditionSchema,
  durationMinutes: finite.min(0).max(525600),
  recoveryMinutes: finite.min(0).max(525600),
  recovery: z.enum(['automatic', 'manual']),
  severity: z.enum(['warning', 'critical']),
  actions: z.object({
    notifyInApp: z.boolean(),
    notifyEmail: z.boolean(),
    notifySms: z.boolean().optional(),
    notifyOnRecovery: z.boolean(),
    reminderMinutes: finite.min(15).max(525600).nullable(),
    recipients: z.literal('responsibles'),
  }),
  source: z.enum(['telemetry', 'sms', 'activity']).optional(),
});
export const ruleSchema: z.ZodType<UnifiedRule> = ruleDefinitionSchema.extend({
  id: z.string().min(1),
  version: finite.int().nonnegative(),
  updatedAt: timestamp,
  updatedBy: z.string().nullable(),
  isDefault: z.boolean(),
});
export const incidentSchema: z.ZodType<UnifiedIncident> = z.object({
  id: z.string().min(1),
  orgId: z.string(),
  sensorId: z.string(),
  sensorTitle: z.string(),
  orgTitle: z.string(),
  deviceId: z.string(),
  sensorType: z.string(),
  ruleId: z.string(),
  ruleTitle: z.string(),
  ruleVersion: finite.int().nonnegative(),
  faultKey: z.string(),
  mode: z.enum(['live', 'test']),
  severity: z.enum(['warning', 'critical']),
  status: workStatusSchema,
  conditionState: z.enum(['firing', 'recovered', 'unknown']),
  openedAt: finite,
  lastObservedAt: finite,
  resolvedAt: timestamp,
  updatedAt: finite,
  version: finite.int().nonnegative(),
  occurrenceCount: finite.int().positive(),
  resolutionReason: z.enum(['recovered', 'fixed', 'false_alarm', 'not_in_use', 'other']).nullable(),
  resolutionNote: z.string(),
  detailPath: z.string(),
});
export const capabilitySchema: z.ZodType<UnifiedCapability> = z.object({
  sensorType: z.string(),
  title: z.string(),
  heartbeatSupported: z.boolean(),
  metrics: z.array(z.string()),
  faults: z.array(z.string()),
  messageSupported: z.boolean(),
});
export const rulesOverviewSchema = z.object({
  rules: z.array(ruleSchema),
  capabilities: z.array(capabilitySchema),
  nextCursor: cursor,
});
export const timelineEventSchema: z.ZodType<UnifiedTimelineEvent> = z.object({
  id: z.string(),
  incidentId: z.string(),
  type: z.enum([
    'opened',
    'observation',
    'recovered',
    'unknown',
    'acknowledged',
    'in_progress',
    'resolved',
    'reminder',
  ]),
  at: finite,
  observedAt: finite,
  actor: z.string(),
  note: z.string(),
  observationId: z.string().nullable(),
  sourceRefs: z.array(z.string()),
  value: z.union([finite, z.boolean(), z.string(), z.null()]),
  ruleVersion: finite.int().nonnegative(),
});
export const historySchema = z.object({
  incident: incidentSchema,
  timeline: z.array(timelineEventSchema),
  nextCursor: cursor,
});
export const heartbeatSchema: z.ZodType<UnifiedHeartbeat> = z.object({
  status: z.enum(['on_time', 'overdue', 'never_seen', 'unknown', 'paused', 'unsupported']),
  lastDataAt: timestamp,
  expectedAfter: timestamp,
  maxAgeMinutes: finite.nullable(),
  ruleId: z.string().nullable(),
  evaluatedAt: finite,
});
const assessmentSchema: z.ZodType<ConditionAssessment> = z.object({
  state: z.enum(['firing', 'recovered', 'unknown']),
  explanation: z.string(),
  faultKey: z.string(),
  evidenceAt: finite,
  value: z.union([finite, z.boolean(), z.string(), z.null()]),
});
export const previewSchema = z.object({
  assessments: z.array(assessmentSchema),
  state: z.enum(['firing', 'recovered', 'unknown']),
  explanation: z.string(),
  heartbeat: heartbeatSchema,
});
export const overviewSchema = z.object({
  incidents: z.array(incidentSchema),
  nextCursor: cursor,
  evaluatedAt: timestamp,
});

export type AlertMode = z.infer<typeof alertModeSchema>;
export type WorkStatus = z.infer<typeof workStatusSchema>;
export type RuleDefinition = UnifiedRuleDefinition;
export type AlertRule = UnifiedRule;
export type Incident = UnifiedIncident;
export type Capability = UnifiedCapability;
export type TimelineEvent = UnifiedTimelineEvent;
export type RulesOverview = z.infer<typeof rulesOverviewSchema>;
export type IncidentHistory = z.infer<typeof historySchema>;
export type RulePreview = z.infer<typeof previewSchema>;
export type AlertOverview = z.infer<typeof overviewSchema>;
export type SensorScope = { orgId: string; sensorId: string };
export type IncidentFilters = Partial<SensorScope> & { mode?: AlertMode; status?: WorkStatus };
export type SaveRule = { id?: string; expectedVersion: number; rule: RuleDefinition };
export type UpdateIncident = {
  incidentId: string;
  expectedVersion: number;
  status: WorkStatus;
  resolutionReason: null | 'recovered' | 'fixed' | 'false_alarm' | 'not_in_use' | 'other';
  note: string;
};
